"use client";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { m, useSpring } from "motion/react";

// ─── STRATEGY: Diagnóstico Profundo ───
// A "digital candy" LeadCard with an internal income graph.
// Idle: chaotic market noise. Midas touch: exponential golden growth.

// ─── Constants ───
const CARD_W = 160;
const CARD_H = 120;
const CARD_CENTER_X = 200;
const CARD_CENTER_Y = 170;
const GLASS_RADIUS = 24;

// ─── Income Graph Lines ───
// Coordinate system: x ∈ [0, CARD_W], y ∈ [0, CARD_H]
// y=0 is TOP of SVG (high value), y=CARD_H is BOTTOM (low value)
// Chaotic: volatile market noise
// Resolved: exponential growth (starts bottom-left, shoots to top-right)
const graphLines = [
    {
        tangled: `M 0 ${CARD_H * 0.5} Q ${CARD_W * 0.1} ${CARD_H * 0.3} ${CARD_W * 0.2} ${CARD_H * 0.6} T ${CARD_W * 0.4} ${CARD_H * 0.4} T ${CARD_W * 0.6} ${CARD_H * 0.7} T ${CARD_W * 0.8} ${CARD_H * 0.35} T ${CARD_W} ${CARD_H * 0.55}`,
        resolved: `M 0 ${CARD_H * 0.95} Q ${CARD_W * 0.6} ${CARD_H * 0.9} ${CARD_W} ${CARD_H * 0.05}`,
    },
    {
        tangled: `M 0 ${CARD_H * 0.7} Q ${CARD_W * 0.15} ${CARD_H * 0.5} ${CARD_W * 0.25} ${CARD_H * 0.8} T ${CARD_W * 0.45} ${CARD_H * 0.55} T ${CARD_W * 0.65} ${CARD_H * 0.85} T ${CARD_W * 0.85} ${CARD_H * 0.5} T ${CARD_W} ${CARD_H * 0.7}`,
        resolved: `M 0 ${CARD_H * 0.95} Q ${CARD_W * 0.65} ${CARD_H * 0.92} ${CARD_W} ${CARD_H * 0.15}`,
    },
    {
        tangled: `M 0 ${CARD_H * 0.4} Q ${CARD_W * 0.12} ${CARD_H * 0.6} ${CARD_W * 0.22} ${CARD_H * 0.3} T ${CARD_W * 0.5} ${CARD_H * 0.65} T ${CARD_W * 0.7} ${CARD_H * 0.25} T ${CARD_W * 0.9} ${CARD_H * 0.6} T ${CARD_W} ${CARD_H * 0.4}`,
        resolved: `M 0 ${CARD_H * 0.95} Q ${CARD_W * 0.7} ${CARD_H * 0.93} ${CARD_W} ${CARD_H * 0.25}`,
    },
    {
        tangled: `M 0 ${CARD_H * 0.6} Q ${CARD_W * 0.08} ${CARD_H * 0.8} ${CARD_W * 0.18} ${CARD_H * 0.45} T ${CARD_W * 0.38} ${CARD_H * 0.75} T ${CARD_W * 0.58} ${CARD_H * 0.4} T ${CARD_W * 0.78} ${CARD_H * 0.72} T ${CARD_W} ${CARD_H * 0.5}`,
        resolved: `M 0 ${CARD_H * 0.95} Q ${CARD_W * 0.75} ${CARD_H * 0.94} ${CARD_W} ${CARD_H * 0.35}`,
    },
    {
        tangled: `M 0 ${CARD_H * 0.8} Q ${CARD_W * 0.1} ${CARD_H * 0.65} ${CARD_W * 0.3} ${CARD_H * 0.9} T ${CARD_W * 0.5} ${CARD_H * 0.6} T ${CARD_W * 0.7} ${CARD_H * 0.85} T ${CARD_W * 0.85} ${CARD_H * 0.55} T ${CARD_W} ${CARD_H * 0.75}`,
        resolved: `M 0 ${CARD_H * 0.95} Q ${CARD_W * 0.8} ${CARD_H * 0.95} ${CARD_W} ${CARD_H * 0.45}`,
    },
];

// ─── Graph Line Component ───
function GraphLine({ tangled, resolved, isResolved, delay }: {
    tangled: string; resolved: string; isResolved: boolean; delay: number;
}) {
    return (
        <m.path
            d={isResolved ? resolved : tangled}
            stroke={isResolved ? "#d4af37" : "#444"}
            strokeWidth={isResolved ? 2.5 : 1}
            strokeLinecap="round"
            fill="none"
            filter={isResolved ? "url(#gold-glow)" : undefined}
            animate={{
                d: isResolved ? resolved : tangled,
                stroke: isResolved ? "#d4af37" : "#444",
                strokeWidth: isResolved ? 2.5 : 1,
            }}
            transition={{
                d: { duration: 0.8, ease: "easeInOut", delay: delay * 0.05 },
                stroke: { duration: 0.4 },
                strokeWidth: { duration: 0.4 },
            }}
        />
    );
}

// ─── Lead Card ("Digital Candy") ───
// Beautiful dark glass card. No real text — just abstract decorative elements
// that feel like it's storing something precious.
function LeadCard({ glowing, isHovered }: { glowing: boolean; isHovered: boolean }) {
    const isActive = glowing || isHovered;

    // Card dimensions — fixed size, the graph fills it edge-to-edge
    const cardX = CARD_CENTER_X - CARD_W / 2;
    const cardY = CARD_CENTER_Y - CARD_H / 2;

    return (
        <g>
            {/* ── Card Shadow ── */}
            <rect
                x={cardX + 4} y={cardY + 6}
                width={CARD_W} height={CARD_H} rx="12"
                fill="black" fillOpacity="0.4"
                filter="url(#card-shadow)"
            />

            {/* ── Card Body (Dark Glass) ── */}
            <m.rect
                x={cardX} y={cardY}
                width={CARD_W} height={CARD_H} rx="12"
                fill="url(#card-bg)"
                animate={{
                    stroke: isActive ? "#d4af37" : "#2a2a2a",
                    strokeWidth: isActive ? 1.5 : 0.5,
                }}
                transition={{ duration: 0.4 }}
            />

            {/* ── Inner Gradient Shimmer ── */}
            <m.rect
                x={cardX} y={cardY}
                width={CARD_W} height={CARD_H} rx="12"
                fill="url(#inner-shimmer)"
                animate={{ opacity: isActive ? 0.15 : 0.03 }}
                transition={{ duration: 0.6 }}
            />

            {/* ── Income Graph (Edge-to-Edge) ── */}
            <svg
                x={cardX} y={cardY}
                width={CARD_W} height={CARD_H}
                viewBox={`0 0 ${CARD_W} ${CARD_H}`}
                preserveAspectRatio="none"
            >
                <defs>
                    <clipPath id="graph-clip">
                        <rect width={CARD_W} height={CARD_H} rx="12" />
                    </clipPath>
                </defs>
                <g clipPath="url(#graph-clip)">
                    {/* Subtle grid lines */}
                    {[0.25, 0.5, 0.75].map((frac) => (
                        <line
                            key={`h-${frac}`}
                            x1="0" y1={CARD_H * frac}
                            x2={CARD_W} y2={CARD_H * frac}
                            stroke="#ffffff" strokeOpacity="0.04" strokeWidth="0.5"
                        />
                    ))}
                    {[0.25, 0.5, 0.75].map((frac) => (
                        <line
                            key={`v-${frac}`}
                            x1={CARD_W * frac} y1="0"
                            x2={CARD_W * frac} y2={CARD_H}
                            stroke="#ffffff" strokeOpacity="0.04" strokeWidth="0.5"
                        />
                    ))}

                    {/* Graph Lines */}
                    {graphLines.map((line, i) => (
                        <GraphLine
                            key={i}
                            tangled={line.tangled}
                            resolved={line.resolved}
                            isResolved={isActive}
                            delay={i}
                        />
                    ))}
                </g>
            </svg>

            {/* ── Decorative Elements ("Digital Candy") ── */}
            {/* Top row: small dot + skeleton bar (feels like storing data) */}
            <m.circle
                cx={cardX + 14} cy={cardY + 14} r="4"
                animate={{
                    fill: isActive ? "#d4af37" : "#333",
                    fillOpacity: isActive ? 1 : 0.6,
                }}
                transition={{ duration: 0.3 }}
            />
            <m.circle
                cx={cardX + 14} cy={cardY + 14} r="6"
                fill="none"
                animate={{
                    stroke: isActive ? "#d4af37" : "#444",
                    strokeOpacity: isActive ? 0.4 : 0.15,
                }}
                strokeWidth="1"
                transition={{ duration: 0.3 }}
            />

            {/* Skeleton bars — abstract, no labels */}
            <rect x={cardX + 26} y={cardY + 11} width="40" height="3" rx="1.5" fill="#333" fillOpacity="0.6" />
            <rect x={cardX + 26} y={cardY + 17} width="25" height="2" rx="1" fill="#333" fillOpacity="0.3" />

            {/* Top-right status indicator */}
            <m.g animate={{ opacity: isActive ? 1 : 0.3 }} transition={{ duration: 0.4 }}>
                <rect x={cardX + CARD_W - 36} y={cardY + 10} width="24" height="8" rx="4" fill="#1a1a1a" stroke="#333" strokeWidth="0.5" />
                <m.circle
                    cx={cardX + CARD_W - 28} cy={cardY + 14} r="2.5"
                    animate={{ fill: isActive ? "#4ade80" : "#555" }}
                    transition={{ duration: 0.3 }}
                />
                <rect x={cardX + CARD_W - 24} y={cardY + 13} width="10" height="2" rx="1" fill="#555" fillOpacity="0.5" />
            </m.g>

            {/* ── Tracing Border (Visible when active) ── */}
            {isActive && (
                <m.rect
                    x={cardX} y={cardY}
                    width={CARD_W} height={CARD_H} rx="12"
                    fill="none"
                    stroke="#d4af37"
                    strokeWidth="1.5"
                    strokeDasharray="30 80"
                    initial={{ strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: -200 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
            )}
        </g>
    );
}

// ─── Magnifying Glass ───
function MagnifyingGlass({ x, y }: { x: ReturnType<typeof useSpring>; y: ReturnType<typeof useSpring> }) {
    return (
        <m.g style={{ x, y }}>
            <circle cx="0" cy="0" r={GLASS_RADIUS + 12} fill="#d4af37" fillOpacity="0.06" />
            <circle cx="0" cy="0" r={GLASS_RADIUS + 6} fill="#d4af37" fillOpacity="0.03" />
            <circle
                cx="0" cy="0" r={GLASS_RADIUS}
                fill="url(#glass-gradient)"
                stroke="#d4af37" strokeWidth="2" strokeOpacity="0.8"
            />
            <line
                x1={GLASS_RADIUS * 0.7} y1={GLASS_RADIUS * 0.7}
                x2={GLASS_RADIUS * 0.7 + 24} y2={GLASS_RADIUS * 0.7 + 24}
                stroke="#d4af37" strokeWidth="4" strokeLinecap="round" strokeOpacity="0.8"
            />
            <path
                d={`M -${GLASS_RADIUS * 0.6} -${GLASS_RADIUS * 0.2} Q -${GLASS_RADIUS * 0.3} -${GLASS_RADIUS * 0.7} ${GLASS_RADIUS * 0.2} -${GLASS_RADIUS * 0.6}`}
                stroke="white" strokeOpacity="0.15" strokeWidth="3" strokeLinecap="round"
            />
        </m.g>
    );
}

// ─── Main Export ───
export const StrategyVisual = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isHovering, setIsHovering] = useState(false);
    const mouseX = useRef(CARD_CENTER_X);
    const mouseY = useRef(CARD_CENTER_Y - 40);

    const glassX = useSpring(CARD_CENTER_X, { stiffness: 12, damping: 25 });
    const glassY = useSpring(CARD_CENTER_Y - 40, { stiffness: 12, damping: 25 });

    const [isNearCard, setIsNearCard] = useState(false);

    // Autonomous idle movement
    useEffect(() => {
        if (isHovering) return;
        let frame: number;
        let t = 0;
        const animate = () => {
            t += 0.005;
            const ix = CARD_CENTER_X + Math.sin(t) * 100;
            const iy = CARD_CENTER_Y + Math.cos(t * 0.5) * 60;
            glassX.set(ix);
            glassY.set(iy);
            const dx = ix - CARD_CENTER_X;
            const dy = iy - CARD_CENTER_Y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            setIsNearCard(dist < 60);
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [isHovering, glassX, glassY]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const svgWidth = 400;
        const svgHeight = 340;
        mouseX.current = ((e.clientX - rect.left) / rect.width) * svgWidth;
        mouseY.current = ((e.clientY - rect.top) / rect.height) * svgHeight;
        glassX.set(mouseX.current);
        glassY.set(mouseY.current);
        const dx = mouseX.current - CARD_CENTER_X;
        const dy = mouseY.current - CARD_CENTER_Y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        setIsNearCard(dist < 90);
    }, [glassX, glassY]);

    return (
        <div
            ref={containerRef}
            className="absolute inset-0 flex items-center justify-center cursor-none"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => { setIsHovering(false); setIsNearCard(false); }}
            onMouseMove={handleMouseMove}
        >
            <svg
                width="100%" height="100%"
                viewBox="0 0 400 340"
                fill="none"
                className="overflow-visible"
            >
                <defs>
                    {/* Card background: dark glass gradient */}
                    {/* Inner shimmer for glass feel */}
                    <linearGradient id="inner-shimmer" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
                        <stop offset="40%" stopColor="transparent" stopOpacity="0" />
                        <stop offset="100%" stopColor="#d4af37" stopOpacity="0.1" />
                    </linearGradient>
                    {/* Glass gradient */}
                    <linearGradient id="glass-gradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="white" stopOpacity="0.1" />
                        <stop offset="100%" stopColor="white" stopOpacity="0.02" />
                    </linearGradient>
                    {/* Gold glow for resolved lines */}
                    <filter id="gold-glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    {/* Card shadow */}
                    <filter id="card-shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
                    </filter>
                </defs>

                {/* Background Graph (Magnified) */}
                <svg
                    x="0" y="0" width="400" height="340"
                    viewBox={`0 0 ${CARD_W} ${CARD_H}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="opacity-10 pointer-events-none"
                    style={{ overflow: 'visible' }}
                >
                    {graphLines.map((line, i) => (
                        <GraphLine
                            key={`bg-${i}`}
                            tangled={line.tangled}
                            resolved={line.resolved}
                            isResolved={isNearCard || isHovering}
                            delay={i}
                        />
                    ))}
                </svg>

                {/* LeadCard with internal graph */}
                <LeadCard glowing={isNearCard} isHovered={isHovering} />

                {/* Magnifying Glass */}
                <MagnifyingGlass x={glassX} y={glassY} />
            </svg>
        </div>
    );
};
